/**
 * One line of a long operation's newline-delimited response: a `progress` line per batch, then exactly one terminal
 * `result` or `error` line. A stream that ends without a terminal line did not finish: the HTTP status was committed
 * with the first line and cannot say so.
 */
export interface NdjsonLine<TProgress, TResult> {
  type: "progress" | "result" | "error";
  progress?: TProgress;
  result?: TResult;
  message?: string | null;
}

interface ReadNdjsonOptions<TProgress> {
  onProgress?: (progress: TProgress) => void;
  /** Used when an `error` line carries no message. */
  errorMessage: string;
  /** Thrown when the stream ends without a terminal line, which means the connection dropped mid-operation. */
  droppedMessage: string;
}

/**
 * Reads a streamed operation's lines as they arrive and returns its terminal result. The caller has already checked
 * `response.ok`: a failure before the first line is an ordinary problem-details response, not a stream.
 *
 * Progress lines are what keep a minutes-long operation alive: they reset the idle timers of every intermediary, and
 * every figure in them is work the server has already committed. An `error` line, or a stream that ends without a
 * terminal line, is thrown — never reported as success.
 */
export const readNdjsonResult = async <TProgress, TResult>(
  response: Response,
  { onProgress, errorMessage, droppedMessage }: ReadNdjsonOptions<TProgress>
): Promise<TResult> => {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("This browser cannot read a streamed response.");

  // Held in an object rather than plain locals: they are assigned from a callback, which TypeScript's narrowing
  // cannot follow, and the checks after the loop would then be typed against the initial values.
  const terminal: { result: TResult | null; error: string | null } = { result: null, error: null };

  const handleLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const parsed = JSON.parse(trimmed) as NdjsonLine<TProgress, TResult>;
    if (parsed.type === "progress" && parsed.progress) onProgress?.(parsed.progress);
    else if (parsed.type === "result" && parsed.result) terminal.result = parsed.result;
    else if (parsed.type === "error") terminal.error = parsed.message || errorMessage;
  };

  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    // A batch's line can arrive split across chunks, so only whole lines are parsed and the remainder is kept.
    for (let newlineAt = buffer.indexOf("\n"); newlineAt >= 0; newlineAt = buffer.indexOf("\n")) {
      handleLine(buffer.slice(0, newlineAt));
      buffer = buffer.slice(newlineAt + 1);
    }
  }
  handleLine(buffer);

  if (terminal.error) throw new Error(terminal.error);
  if (!terminal.result) throw new Error(droppedMessage);

  return terminal.result;
};
